import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentSeason } from "@/lib/stormsQueries";
import { SVGMapThumbnail } from "@/components/SVGMapViewer";
import { Calendar, Zap, CloudLightning, ArrowRight } from "lucide-react";

// ---------------------------------------------------------------------------
// Utility — format a UTC Date as "Ddd DD Mon" (e.g. "Sun 22 Mar")
// ---------------------------------------------------------------------------
function fmtUTCDate(d) {
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  });
}

// ---------------------------------------------------------------------------
// Utility — compute the max LAI value for a given UTC calendar day from a
// GeoJSON feature-collection and its run init time.
// ---------------------------------------------------------------------------
function maxLAIForDay(features, initTime, dayStart, dayEnd) {
  let maxVal = null;
  features.forEach((f) => {
    const fh = f.properties.forecast_hour;
    const val = parseFloat(
      f.properties.lai_decimal != null ? f.properties.lai_decimal :
      f.properties.lai_value   != null ? f.properties.lai_value   :
      f.properties.lai         != null ? f.properties.lai         : 0
    );
    const vt = new Date(initTime.getTime() + fh * 3600_000);
    if (vt >= dayStart && vt < dayEnd) {
      maxVal = maxVal === null ? val : Math.max(maxVal, val);
    }
  });
  return maxVal;
}

// ---------------------------------------------------------------------------
// Fetch the max LAI for a calendar day (YYYYMMDD string). Tries run hours
// 18z → 12z → 06z → 00z in sequence and returns the first successful result.
// Returns null when no run data is available.
// ---------------------------------------------------------------------------
async function fetchMaxLAIForDate(dateStr) {
  const year  = parseInt(dateStr.slice(0, 4), 10);
  const month = parseInt(dateStr.slice(4, 6), 10) - 1;
  const day   = parseInt(dateStr.slice(6, 8), 10);
  const dayStart = new Date(Date.UTC(year, month, day));
  const dayEnd   = new Date(dayStart.getTime() + 86_400_000);

  for (const h of ["18z", "12z", "06z", "00z"]) {
    try {
      const res = await fetch(`/data/lai/runs/${dateStr}_${h}.json`);
      if (!res.ok) continue;
      const runData = await res.json();
      const features = runData.features || [];
      const initTime = new Date(Date.UTC(year, month, day, parseInt(h, 10)));
      const val = maxLAIForDay(features, initTime, dayStart, dayEnd);
      if (val !== null) return val;
    } catch {
      // try next hour
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// LAI colour map — viridis-based (matches LAIv1Page and home.html)
// ---------------------------------------------------------------------------
const LAI_COLORS = {
  0:  { color: "#6B7280", label: "None"     },
  1:  { color: "#21918c", label: "Weak"     },
  2:  { color: "#2d708e", label: "Weak"     },
  3:  { color: "#3b528b", label: "Moderate" },
  4:  { color: "#443983", label: "Moderate" },
  5:  { color: "#481567", label: "Strong"   },
  6:  { color: "#472d7b", label: "Strong"   },
  7:  { color: "#39568c", label: "Severe"   },
  8:  { color: "#20908d", label: "Severe"   },
  9:  { color: "#5dc963", label: "Extreme"  },
  10: { color: "#fde725", label: "Extreme"  },
};

function laiStyle(val) {
  const k = Math.min(10, Math.max(0, Math.round(val)));
  return LAI_COLORS[k] || LAI_COLORS[0];
}

// ---------------------------------------------------------------------------
// Skeleton block used while data is loading
// ---------------------------------------------------------------------------
function Skeleton({ className = "" }) {
  return (
    <span
      className={`inline-block bg-[#2e2e2e] animate-pulse rounded ${className}`}
    />
  );
}

// ---------------------------------------------------------------------------
// Card 1 — Most Recent Outlook
// ---------------------------------------------------------------------------
function OutlookCard() {
  const [outlook, setOutlook] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        // Try active + published first
        const { data: active } = await supabase
          .from("outlooks")
          .select("id, outlook_date, created_at, is_active, svg_content")
          .eq("is_active", true)
          .eq("is_published", true)
          .maybeSingle();

        if (active) { setOutlook(active); return; }

        // Fallback: most recently published
        const { data: recent } = await supabase
          .from("outlooks")
          .select("id, outlook_date, created_at, is_active, svg_content")
          .eq("is_published", true)
          .order("outlook_date", { ascending: false })
          .limit(1)
          .maybeSingle();

        setOutlook(recent || null);
      } catch {
        setOutlook(null);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const dateLabel = outlook
    ? (() => {
        const raw = outlook.outlook_date || (outlook.created_at ? outlook.created_at.split("T")[0] : null);
        if (!raw) return null;
        const d = new Date(raw + "T00:00:00");
        return isNaN(d.getTime()) ? null : d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
      })()
    : null;

  const slug = outlook ? (outlook.outlook_date || outlook.id) : null;

  return (
    <article className="bg-[#1e1e1e] border border-white/10 flex flex-col p-5 gap-4 relative overflow-hidden" data-testid="dashboard-outlook-card">
      {/* Decorative corner icon */}
      <div className="absolute top-3 right-3 opacity-10 pointer-events-none" aria-hidden="true">
        <Calendar className="w-12 h-12 text-[#9CA3AF]" />
      </div>

      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#9CA3AF] font-medium">
        <Calendar className="w-3 h-3" />
        Outlook
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-heading text-base font-bold text-[#F9FAFB] uppercase tracking-tight">
          Most Recent Outlook
        </h2>
        {!loading && outlook && (
          outlook.is_active ? (
            <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#22C55E] bg-[#22C55E]/10 border border-[#22C55E]/20 px-2 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
              Active
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-widest text-[#6B7280] border border-white/10 px-2 py-0.5">
              Most Recent
            </span>
          )
        )}
      </div>

      {/* Map preview */}
      {loading ? (
        <div className="w-full h-32 bg-[#0B1220] border border-white/10 animate-pulse" />
      ) : (
        <SVGMapThumbnail svgContent={outlook?.svg_content} className="h-32" />
      )}

      {/* Date */}
      <div className="flex items-center gap-1.5 text-xs text-[#9CA3AF]">
        <Calendar className="w-3 h-3 flex-shrink-0" />
        {loading ? <Skeleton className="w-32 h-3" /> : (dateLabel || "—")}
      </div>

      <div className="mt-auto pt-2">
        {slug ? (
          <Link
            to={`/outlook/${slug}`}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#FBBF24] hover:text-[#F59E0B] transition-colors"
          >
            View Outlook <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        ) : (
          <Link
            to="/outlook"
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#FBBF24] hover:text-[#F59E0B] transition-colors"
          >
            View Outlook <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Card 2 — LAI Forecast
// ---------------------------------------------------------------------------
function LAICard() {
  const [todayMax, setTodayMax] = useState(null);
  const [tomMax, setTomMax] = useState(null);
  const [runInfo, setRunInfo] = useState(null);
  const [error, setError] = useState(false);
  // 7-day history: array of { date: Date, max: number|null, loading: bool }
  const [history, setHistory] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const idxRes = await fetch("/data/lai/index.json");
        if (!idxRes.ok) throw new Error("index fetch failed");
        const idx = await idxRes.json();
        const run = idx.latest;
        if (!run || !run.includes("_")) throw new Error("invalid run format");

        const dp = run.split("_")[0];
        const hp = run.split("_")[1];
        const init = new Date(
          Date.UTC(
            parseInt(dp.slice(0, 4), 10),
            parseInt(dp.slice(4, 6), 10) - 1,
            parseInt(dp.slice(6, 8), 10),
            parseInt(hp.replace("z", ""), 10)
          )
        );
        setRunInfo(
          `Run: ${dp.slice(0, 4)}-${dp.slice(4, 6)}-${dp.slice(6, 8)} ${hp.toUpperCase()}`
        );

        const runRes = await fetch(`/data/lai/runs/${run}.json`);
        if (!runRes.ok) throw new Error("run fetch failed");
        const runData = await runRes.json();

        const features = runData.features || [];
        const now = new Date();
        const d0 = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
        );
        const d1 = new Date(d0.getTime() + 86_400_000);
        const d2 = new Date(d1.getTime() + 86_400_000);

        let tMax = 0, tomMaxVal = 0;
        features.forEach((f) => {
          const fh = f.properties.forecast_hour;
          const val = parseFloat(
            f.properties.lai_decimal != null ? f.properties.lai_decimal :
            f.properties.lai_value   != null ? f.properties.lai_value   :
            f.properties.lai         != null ? f.properties.lai         : 0
          );
          const vt = new Date(init.getTime() + fh * 3_600_000);
          if (vt >= d0 && vt < d1) tMax = Math.max(tMax, val);
          else if (vt >= d1 && vt < d2) tomMaxVal = Math.max(tomMaxVal, val);
        });

        setTodayMax(tMax);
        setTomMax(tomMaxVal);
      } catch {
        setError(true);
      }
    }
    load();
  }, []);

  // Load 7-day history (days D-1 through D-7, most recent first)
  useEffect(() => {
    async function loadHistory() {
      const now = new Date();
      const days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - (i + 1)));
        return d;
      });

      // Initialise with loading placeholders
      setHistory(days.map((d) => ({ date: d, max: null, loading: true })));

      // Fetch each day's max LAI (sequentially to avoid burst)
      for (let i = 0; i < days.length; i++) {
        const d = days[i];
        const pad2 = (n) => String(n).padStart(2, "0");
        const dateStr =
          d.getUTCFullYear() +
          pad2(d.getUTCMonth() + 1) +
          pad2(d.getUTCDate());
        const maxVal = await fetchMaxLAIForDate(dateStr);
        setHistory((prev) =>
          prev.map((row, idx) =>
            idx === i ? { ...row, max: maxVal, loading: false } : row
          )
        );
      }
    }
    loadHistory();
  }, []);

  function ValueRow({ label, value }) {
    if (value === null && !error) {
      return (
        <div className="flex items-center justify-between py-2 border-b border-white/5">
          <span className="text-xs text-[#9CA3AF] uppercase tracking-wider">{label}</span>
          <Skeleton className="w-10 h-4" />
        </div>
      );
    }
    if (error || value === null) {
      return (
        <div className="flex items-center justify-between py-2 border-b border-white/5">
          <span className="text-xs text-[#9CA3AF] uppercase tracking-wider">{label}</span>
          <span className="font-mono text-sm text-[#6B7280]">N/A</span>
        </div>
      );
    }
    const s = laiStyle(value);
    return (
      <div className="flex items-center justify-between py-2 border-b border-white/5">
        <span className="text-xs text-[#9CA3AF] uppercase tracking-wider">{label}</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-lg font-bold" style={{ color: s.color }}>
            {value > 0 ? String(value) : "0"}
          </span>
          <span className="text-xs uppercase tracking-wider" style={{ color: s.color }}>
            {s.label}
          </span>
        </div>
      </div>
    );
  }

  return (
    <article className="bg-[#1e1e1e] border border-white/10 flex flex-col p-5 gap-4 relative overflow-hidden" data-testid="dashboard-lai-card">
      {/* Decorative corner icon */}
      <div className="absolute top-3 right-3 opacity-10 pointer-events-none" aria-hidden="true">
        <Zap className="w-12 h-12 text-[#9CA3AF]" />
      </div>

      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#9CA3AF] font-medium">
        <Zap className="w-3 h-3" />
        Lightning Activity Index
      </p>

      <h2 className="font-heading text-base font-bold text-[#F9FAFB] uppercase tracking-tight">
        Maximum LAI Index
      </h2>

      <div className="flex flex-col gap-0">
        <ValueRow label="Today" value={todayMax} />
        <ValueRow label="Tomorrow" value={tomMax} />
      </div>

      {/* 7-day history */}
      {history.length > 0 && (
        <div className="border-t border-white/10 pt-3 flex flex-col gap-0">
          <p className="text-[10px] uppercase tracking-widest text-[#6B7280] font-medium mb-1">
            7-Day Max LAI
          </p>
          {history.map(({ date, max, loading: rowLoading }, i) => {
            const label = fmtUTCDate(date);
            return (
              <div
                key={i}
                className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0"
              >
                <span className="font-mono text-[11px] text-[#9CA3AF]">{label}</span>
                {rowLoading ? (
                  <Skeleton className="w-8 h-3" />
                ) : max === null ? (
                  <span className="font-mono text-[11px] text-[#4B5563]">N/A</span>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span
                      className="font-mono text-[11px] font-semibold"
                      style={{ color: laiStyle(max).color }}
                    >
                      {max.toFixed(1)}
                    </span>
                    <span
                      className="text-[9px] uppercase tracking-wider"
                      style={{ color: laiStyle(max).color }}
                    >
                      {laiStyle(max).label}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {runInfo && (
        <p className="text-[10px] text-[#6B7280] font-mono">{runInfo}</p>
      )}

      <div className="mt-auto flex items-center gap-4 pt-1">
        <Link
          to="/lai-v1"
          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#FBBF24] hover:text-[#F59E0B] transition-colors"
        >
          <Zap className="w-3 h-3" />
          LAI.v1 Forecast
        </Link>
        <a
          href="/data/lai/lai_forecast_latest.gif"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#FBBF24] hover:text-[#F59E0B] transition-colors"
        >
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          Animation
        </a>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Card 4 — Lightning Count
// ---------------------------------------------------------------------------
// Future-ready data structure: { date: string, uk: number|null, eu: number|null }
// null = file exists but no data; undefined key = file not yet fetched.

function LightningCountCard() {
  const [region, setRegion] = useState("uk"); // "uk" | "eu"
  // history: array of { date: Date, uk: number|null|"missing", eu: number|null|"missing", loading: bool }
  // "missing" means the file returned 404 (no data collected yet for that day)
  const [history, setHistory] = useState([]);
  const [allMissing, setAllMissing] = useState(false);

  useEffect(() => {
    async function loadHistory() {
      const now = new Date();
      const days = Array.from({ length: 7 }, (_, i) => {
        return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i));
      });

      setHistory(days.map((d) => ({ date: d, uk: undefined, eu: undefined, loading: true })));

      const pad2 = (n) => String(n).padStart(2, "0");

      const results = await Promise.all(
        days.map(async (d) => {
          const ds = `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
          const [ukRes, euRes] = await Promise.all([
            fetch(`/data/lightning/${ds}.json`).catch(() => null),
            fetch(`/data/lightning/europe/${ds}.json`).catch(() => null),
          ]);

          let ukCount = "missing";
          let euCount = "missing";

          if (ukRes && ukRes.ok) {
            try {
              const j = await ukRes.json();
              ukCount = Array.isArray(j.points) ? j.points.length : null;
            } catch { ukCount = null; }
          }
          if (euRes && euRes.ok) {
            try {
              const j = await euRes.json();
              euCount = Array.isArray(j.points) ? j.points.length : null;
            } catch { euCount = null; }
          }

          return { date: d, uk: ukCount, eu: euCount, loading: false };
        })
      );

      setHistory(results);
      setAllMissing(results.every((r) => r.uk === "missing" && r.eu === "missing"));
    }
    loadHistory();
  }, []);

  const hasAnyData = !allMissing && history.some((r) => r.uk !== "missing" || r.eu !== "missing");

  return (
    <article className="bg-[#1e1e1e] border border-white/10 flex flex-col p-5 gap-4 relative overflow-hidden" data-testid="dashboard-lightning-count-card">
      {/* Decorative corner icon */}
      <div className="absolute top-3 right-3 opacity-10 pointer-events-none" aria-hidden="true">
        <Zap className="w-12 h-12 text-[#9CA3AF]" />
      </div>

      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#9CA3AF] font-medium">
        <Zap className="w-3 h-3" />
        Lightning Count
      </p>

      <div className="flex items-center justify-between gap-2">
        <h2 className="font-heading text-base font-bold text-[#F9FAFB] uppercase tracking-tight">
          Lightning Count
        </h2>
        {/* UK / Europe toggle */}
        <div className="flex items-center border border-white/10 overflow-hidden flex-shrink-0">
          <button
            onClick={() => setRegion("uk")}
            className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold transition-colors ${
              region === "uk"
                ? "bg-[#FBBF24] text-[#0B1220]"
                : "text-[#9CA3AF] hover:text-[#F9FAFB]"
            }`}
          >
            UK
          </button>
          <button
            onClick={() => setRegion("eu")}
            className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold transition-colors ${
              region === "eu"
                ? "bg-[#FBBF24] text-[#0B1220]"
                : "text-[#9CA3AF] hover:text-[#F9FAFB]"
            }`}
          >
            Europe
          </button>
        </div>
      </div>

      {/* Loading state */}
      {history.length === 0 || history.some((r) => r.loading) ? (
        <div className="flex flex-col gap-0">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
              <Skeleton className="w-20 h-3" />
              <Skeleton className="w-8 h-3" />
            </div>
          ))}
        </div>
      ) : allMissing ? (
        /* Empty state — no data collected yet */
        <div className="flex flex-col items-center justify-center py-6 gap-3 text-center">
          <Zap className="w-8 h-8 text-[#4B5563]" />
          <p className="text-sm font-medium text-[#9CA3AF]">No lightning data available yet</p>
          <p className="text-xs text-[#6B7280] leading-relaxed max-w-[200px]">
            Data will appear once lightning history collection begins.
          </p>
          <span className="text-[10px] uppercase tracking-widest text-[#4B5563] border border-white/10 px-2 py-0.5 font-mono">
            Status: Awaiting data
          </span>
        </div>
      ) : (
        /* Data rows */
        <div className="flex flex-col gap-0">
          {history.map(({ date, uk, eu, loading: rowLoading }, i) => {
            const label = fmtUTCDate(date);
            const count = region === "uk" ? uk : eu;
            return (
              <div
                key={i}
                className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0"
              >
                <span className="font-mono text-[11px] text-[#9CA3AF]">{label}</span>
                {rowLoading ? (
                  <Skeleton className="w-8 h-3" />
                ) : count === "missing" ? (
                  <span className="font-mono text-[11px] text-[#4B5563]">--</span>
                ) : count === null ? (
                  <span className="font-mono text-[11px] text-[#4B5563]">N/A</span>
                ) : (
                  <span className="font-mono text-[11px] font-semibold text-[#F9FAFB]">
                    {count.toLocaleString()}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {hasAnyData && (
        <p className="text-[10px] text-[#4B5563] font-mono">
          Counts show detected strikes per 24 h UTC window
        </p>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Card 3 — Named Storm Summary
// ---------------------------------------------------------------------------
function StormCard() {
  const [recentLabel, setRecentLabel] = useState(null);
  const [seasonCount, setSeasonCount] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await supabase
          .from("named_storms")
          .select("storm_name, season, date_named")
          .order("date_named", { ascending: true });

        if (!Array.isArray(data)) { setLoading(false); return; }

        const season = getCurrentSeason();
        const seasonStorms = data.filter((s) => s.season === season);
        const total = data.length;

        const sorted = data.slice().sort((a, b) =>
          (b.date_named || "").localeCompare(a.date_named || "")
        );
        const latest = sorted[0];
        const label = latest && latest.date_named
          ? `${latest.storm_name} (${new Date(latest.date_named + "T00:00:00").toLocaleDateString("en-GB", { month: "short", year: "numeric" })})`
          : "—";

        setRecentLabel(label);
        setSeasonCount(seasonStorms.length);
        setTotalCount(total);
      } catch {
        // leave as loading=false with nulls
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function StatRow({ label, children }) {
    return (
      <div className="flex items-center justify-between py-2 border-b border-white/5">
        <span className="text-xs text-[#9CA3AF] uppercase tracking-wider">{label}</span>
        <div className="text-right">
          {loading ? <Skeleton className="w-16 h-4" /> : children}
        </div>
      </div>
    );
  }

  return (
    <article className="bg-[#1e1e1e] border border-white/10 flex flex-col p-5 gap-4 relative overflow-hidden" data-testid="dashboard-storm-card">
      {/* Decorative corner icon */}
      <div className="absolute top-3 right-3 opacity-10 pointer-events-none" aria-hidden="true">
        <CloudLightning className="w-12 h-12 text-[#9CA3AF]" />
      </div>

      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#9CA3AF] font-medium">
        <CloudLightning className="w-3 h-3" />
        Named Storm Tracker
      </p>

      <h2 className="font-heading text-base font-bold text-[#F9FAFB] uppercase tracking-tight">
        Named Storm Summary
      </h2>

      <div className="flex flex-col gap-0">
        <StatRow label="Most Recent Storm">
          <span className="font-mono text-sm text-[#F9FAFB]">{recentLabel || "—"}</span>
        </StatRow>
        <StatRow label="This Season">
          <div className="flex items-baseline gap-1.5">
            <span className="font-mono text-2xl font-bold text-[#F9FAFB]">
              {seasonCount ?? "—"}
            </span>
            <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">storms</span>
          </div>
        </StatRow>
        <StatRow label="All-Time Total">
          <div className="flex items-baseline gap-1.5">
            <span className="font-mono text-2xl font-bold text-[#F9FAFB]">
              {totalCount ?? "—"}
            </span>
            <span className="text-[10px] text-[#6B7280] uppercase tracking-wider">storms</span>
          </div>
        </StatRow>
      </div>

      <div className="mt-auto pt-1">
        <Link
          to="/storms"
          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#FBBF24] hover:text-[#F59E0B] transition-colors"
        >
          Storm Database <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Main DashboardPage
// ---------------------------------------------------------------------------
export default function DashboardPage() {
  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      {/* Page header */}
      <div className="border-b border-white/5 py-8">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#F9FAFB] uppercase tracking-tight mb-1">
            UK Weather Dashboard
          </h1>
          <p className="flex items-center gap-1.5 text-xs text-[#9CA3AF] font-mono">
            <Calendar className="w-3.5 h-3.5" />
            {today}
          </p>
        </div>
      </div>

      {/* 4-card grid */}
      <section className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <OutlookCard />
          <LAICard />
          <LightningCountCard />
          <StormCard />
        </div>
      </section>
    </div>
  );
}
